import { Component, createRef } from 'react';
import sharedStyles from '../single-page.module.scss';
import styles from './color-converter.component.module.scss';
import { genStyles } from '../converter-layout/generator-layout';

interface ColorState {
    input: string;
    hex: string; rgb: string; hsl: string; cmyk: string; css: string;
    r: number; g: number; b: number;
    hue: number; sat: number; light: number;
    error: string;
    copiedField: string | null;
    draggingSV: boolean;
    draggingHue: boolean;
}

function clamp(v: number, min: number, max: number) { return Math.max(min, Math.min(max, v)); }
function rgbToHex(r: number, g: number, b: number) { return '#' + [r, g, b].map(v => clamp(v, 0, 255).toString(16).padStart(2, '0')).join('').toUpperCase(); }

function rgbToHsl(r: number, g: number, b: number) {
    r /= 255; g /= 255; b /= 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b), l = (max + min) / 2;
    if (max === min) return { h: 0, s: 0, l: Math.round(l * 100) };
    const d = max - min, s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    let h = 0;
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
    return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
}

function rgbToCmyk(r: number, g: number, b: number) {
    if (r === 0 && g === 0 && b === 0) return { c: 0, m: 0, y: 0, k: 100 };
    const c1 = 1 - r / 255, m1 = 1 - g / 255, y1 = 1 - b / 255, k = Math.min(c1, m1, y1);
    return { c: Math.round(((c1 - k) / (1 - k)) * 100), m: Math.round(((m1 - k) / (1 - k)) * 100), y: Math.round(((y1 - k) / (1 - k)) * 100), k: Math.round(k * 100) };
}

function hslToRgb(h: number, s: number, l: number) {
    s /= 100; l /= 100;
    const a = s * Math.min(l, 1 - l);
    const f = (n: number) => { const k = (n + h / 30) % 12; return l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1); };
    return { r: Math.round(f(0) * 255), g: Math.round(f(8) * 255), b: Math.round(f(4) * 255) };
}

function hsvToRgb(h: number, s: number, v: number) {
    s /= 100; v /= 100;
    const c = v * s, x = c * (1 - Math.abs((h / 60) % 2 - 1)), m = v - c;
    let r = 0, g = 0, b = 0;
    if (h < 60) { r = c; g = x; } else if (h < 120) { r = x; g = c; }
    else if (h < 180) { g = c; b = x; } else if (h < 240) { g = x; b = c; }
    else if (h < 300) { r = x; b = c; } else { r = c; b = x; }
    return { r: Math.round((r + m) * 255), g: Math.round((g + m) * 255), b: Math.round((b + m) * 255) };
}

function rgbToHsv(r: number, g: number, b: number) {
    r /= 255; g /= 255; b /= 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b), d = max - min;
    let h = 0;
    if (d !== 0) {
        if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) * 60;
        else if (max === g) h = ((b - r) / d + 2) * 60;
        else h = ((r - g) / d + 4) * 60;
    }
    return { h: Math.round(h), s: max === 0 ? 0 : Math.round((d / max) * 100), v: Math.round(max * 100) };
}

function cmykToRgb(c: number, m: number, y: number, k: number) {
    return { r: Math.round(255 * (1 - c / 100) * (1 - k / 100)), g: Math.round(255 * (1 - m / 100) * (1 - k / 100)), b: Math.round(255 * (1 - y / 100) * (1 - k / 100)) };
}

const CSS_COLORS: Record<string, [number, number, number]> = {
    red: [255,0,0], green: [0,128,0], blue: [0,0,255], white: [255,255,255], black: [0,0,0],
    yellow: [255,255,0], cyan: [0,255,255], magenta: [255,0,255], orange: [255,165,0], purple: [128,0,128],
    pink: [255,192,203], gray: [128,128,128], grey: [128,128,128], navy: [0,0,128], teal: [0,128,128],
    indigo: [75,0,130], coral: [255,127,80], salmon: [250,128,114], gold: [255,215,0], tomato: [255,99,71],
};

function parseColor(input: string): { r: number; g: number; b: number } | null {
    const s = input.trim().toLowerCase();
    if (!s) return null;
    if (CSS_COLORS[s]) { const [r, g, b] = CSS_COLORS[s]; return { r, g, b }; }
    const hexMatch = s.match(/^#?([0-9a-f]{3}|[0-9a-f]{6})$/);
    if (hexMatch) { let h = hexMatch[1]; if (h.length === 3) h = h[0]+h[0]+h[1]+h[1]+h[2]+h[2]; return { r: parseInt(h.slice(0,2),16), g: parseInt(h.slice(2,4),16), b: parseInt(h.slice(4,6),16) }; }
    const rgbMatch = s.match(/^rgb\s*\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/);
    if (rgbMatch) { const [,r,g,b] = rgbMatch.map(Number); return (r<=255&&g<=255&&b<=255) ? {r,g,b} : null; }
    const hslMatch = s.match(/^hsl\s*\(\s*(\d{1,3})\s*,\s*(\d{1,3})%?\s*,\s*(\d{1,3})%?\s*\)$/);
    if (hslMatch) { const [,h,sat,l] = hslMatch.map(Number); return (h<=360&&sat<=100&&l<=100) ? hslToRgb(h,sat,l) : null; }
    const cmykMatch = s.match(/^cmyk\s*\(\s*(\d{1,3})%?\s*,\s*(\d{1,3})%?\s*,\s*(\d{1,3})%?\s*,\s*(\d{1,3})%?\s*\)$/);
    if (cmykMatch) { const [,c,m,y,k] = cmykMatch.map(Number); return (c<=100&&m<=100&&y<=100&&k<=100) ? cmykToRgb(c,m,y,k) : null; }
    const plain = s.match(/^([0-9a-f]{6})$/);
    if (plain) return { r: parseInt(s.slice(0,2),16), g: parseInt(s.slice(2,4),16), b: parseInt(s.slice(4,6),16) };
    return null;
}

function formatOutputs(r: number, g: number, b: number) {
    const hex = rgbToHex(r, g, b);
    const { h, s, l } = rgbToHsl(r, g, b);
    const { c, m, y, k } = rgbToCmyk(r, g, b);
    return { hex, rgb: `rgb(${r}, ${g}, ${b})`, hsl: `hsl(${h}, ${s}%, ${l}%)`, cmyk: `cmyk(${c}%, ${m}%, ${y}%, ${k}%)`, css: `--color: ${hex};` };
}

export default class ColorConverterComponent extends Component<{}, ColorState> {
    private svCanvasRef = createRef<HTMLCanvasElement>();
    private hueCanvasRef = createRef<HTMLCanvasElement>();
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;

    constructor(props: {}) {
        super(props);
        const r = 99, g = 102, b = 241;
        const hsv = rgbToHsv(r, g, b);
        this.state = { input: '#6366F1', r, g, b, hue: hsv.h, sat: hsv.s, light: hsv.v, ...formatOutputs(r, g, b), error: '', copiedField: null, draggingSV: false, draggingHue: false };
    }

    componentDidMount() { this.drawSV(); this.drawHue(); }
    componentDidUpdate(_: any, prev: ColorState) {
        if (prev.hue !== this.state.hue || prev.sat !== this.state.sat || prev.light !== this.state.light) this.drawSV();
    }

    drawSV() {
        const canvas = this.svCanvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        const w = canvas.width = canvas.offsetWidth;
        const h = canvas.height = canvas.offsetHeight;
        // White to hue gradient (horizontal)
        const hueRgb = hsvToRgb(this.state.hue, 100, 100);
        const grad1 = ctx.createLinearGradient(0, 0, w, 0);
        grad1.addColorStop(0, '#fff');
        grad1.addColorStop(1, `rgb(${hueRgb.r},${hueRgb.g},${hueRgb.b})`);
        ctx.fillStyle = grad1;
        ctx.fillRect(0, 0, w, h);
        // Black gradient (vertical)
        const grad2 = ctx.createLinearGradient(0, 0, 0, h);
        grad2.addColorStop(0, 'rgba(0,0,0,0)');
        grad2.addColorStop(1, '#000');
        ctx.fillStyle = grad2;
        ctx.fillRect(0, 0, w, h);
        // Draw cursor
        const cx = (this.state.sat / 100) * w;
        const cy = (1 - this.state.light / 100) * h;
        ctx.beginPath();
        ctx.arc(cx, cy, 7, 0, Math.PI * 2);
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 2;
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(cx, cy, 8, 0, Math.PI * 2);
        ctx.strokeStyle = '#000';
        ctx.lineWidth = 1;
        ctx.stroke();
    }

    drawHue() {
        const canvas = this.hueCanvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        const w = canvas.width = canvas.offsetWidth;
        const h = canvas.height = canvas.offsetHeight;
        const grad = ctx.createLinearGradient(0, 0, w, 0);
        for (let i = 0; i <= 6; i++) grad.addColorStop(i / 6, `hsl(${i * 60}, 100%, 50%)`);
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, w, h);
        // Draw cursor
        const cx = (this.state.hue / 360) * w;
        ctx.beginPath();
        ctx.rect(cx - 3, 0, 6, h);
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 2;
        ctx.stroke();
        ctx.strokeStyle = '#000';
        ctx.lineWidth = 1;
        ctx.stroke();
    }

    updateFromHSV(hue: number, sat: number, v: number) {
        const { r, g, b } = hsvToRgb(hue, sat, v);
        const outputs = formatOutputs(r, g, b);
        this.setState({ hue, sat, light: v, r, g, b, input: outputs.hex, ...outputs, error: '' });
    }

    private getXY(e: React.MouseEvent | React.TouchEvent, canvas: HTMLCanvasElement) {
        const rect = canvas.getBoundingClientRect();
        const clientX = 'touches' in e ? e.touches[0].clientX : (e as React.MouseEvent).clientX;
        const clientY = 'touches' in e ? e.touches[0].clientY : (e as React.MouseEvent).clientY;
        return { x: clientX - rect.left, y: clientY - rect.top, w: rect.width, h: rect.height };
    }

    handleSVStart = (e: React.MouseEvent | React.TouchEvent) => {
        this.setState({ draggingSV: true });
        this.handleSVMove(e);
    };
    handleSVMove = (e: React.MouseEvent | React.TouchEvent) => {
        if (!this.state.draggingSV && !('mousedown' === e.type || 'touchstart' === e.type)) return;
        const { x, y, w, h } = this.getXY(e, this.svCanvasRef.current!);
        const s = clamp((x / w) * 100, 0, 100);
        const v = clamp((1 - y / h) * 100, 0, 100);
        this.updateFromHSV(this.state.hue, Math.round(s), Math.round(v));
    };

    handleHueStart = (e: React.MouseEvent | React.TouchEvent) => {
        this.setState({ draggingHue: true });
        this.handleHueMove(e);
    };
    handleHueMove = (e: React.MouseEvent | React.TouchEvent) => {
        if (!this.state.draggingHue && !('mousedown' === e.type || 'touchstart' === e.type)) return;
        const { x, w } = this.getXY(e, this.hueCanvasRef.current!);
        const h = clamp((x / w) * 360, 0, 360);
        this.updateFromHSV(Math.round(h), this.state.sat, this.state.light);
        this.drawHue();
    };

    handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const input = e.target.value;
        const parsed = parseColor(input);
        if (parsed) {
            const { r, g, b } = parsed;
            const hsv = rgbToHsv(r, g, b);
            this.setState({ input, r, g, b, hue: hsv.h, sat: hsv.s, light: hsv.v, ...formatOutputs(r, g, b), error: '' });
        } else if (!input.trim()) {
            this.setState({ input, error: '' });
        } else {
            this.setState({ input, error: 'Try #FF5733, rgb(255,87,51), hsl(11,100%,60%), or a color name' });
        }
    };

    copyToClipboard = (value: string, field: string) => {
        navigator.clipboard.writeText(value).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copiedField: field });
            this.copyTimeout = setTimeout(() => this.setState({ copiedField: null }), 2000);
        });
    };

    renderOutputRow(label: string, value: string, field: string) {
        const isCopied = this.state.copiedField === field;
        return (
            <div className={styles.outputRow} key={field}>
                <span className={styles.outputLabel}>{label}</span>
                <code className={styles.outputValue}>{value}</code>
                <button className={`${sharedStyles.copyButton} ${isCopied ? sharedStyles.copyButtonSuccess : ''}`}
                    onClick={() => this.copyToClipboard(value, field)}>{isCopied ? '✓ Copied' : 'Copy'}</button>
            </div>
        );
    }

    render() {
        const { input, hex, rgb, hsl, cmyk, css, r, g, b, error } = this.state;

        return (
            <div className="row">
                <div className="col-12">
                    <div className={genStyles.section}>
                        <canvas ref={this.svCanvasRef} className={styles.svCanvas}
                            onMouseDown={this.handleSVStart}
                            onMouseMove={this.handleSVMove}
                            onMouseUp={() => this.setState({ draggingSV: false })}
                            onMouseLeave={() => this.setState({ draggingSV: false })}
                            onTouchStart={this.handleSVStart}
                            onTouchMove={this.handleSVMove}
                            onTouchEnd={() => this.setState({ draggingSV: false })} />
                        <canvas ref={this.hueCanvasRef} className={styles.hueBar}
                            onMouseDown={this.handleHueStart}
                            onMouseMove={this.handleHueMove}
                            onMouseUp={() => this.setState({ draggingHue: false })}
                            onMouseLeave={() => this.setState({ draggingHue: false })}
                            onTouchStart={this.handleHueStart}
                            onTouchMove={this.handleHueMove}
                            onTouchEnd={() => this.setState({ draggingHue: false })} />
                    </div>
                </div>
                <div className="col-12">
                    <div className={genStyles.section}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            <label className={sharedStyles.label} htmlFor="color-input" style={{ margin: 0, whiteSpace: 'nowrap' }}>Enter any color</label>
                            <input id="color-input" type="text" className={styles.textInput}
                                placeholder="#6366F1, rgb(99,102,241), red..."
                                value={input} onChange={this.handleInput} autoComplete="off" spellCheck={false} />
                        </div>
                        {error && <div className={sharedStyles.errorMessage} aria-live="polite">⚠ {error}</div>}
                    </div>
                </div>
                <div className="col-12">
                    <div className={genStyles.section}>
                        <div className={styles.swatch} style={{ backgroundColor: rgbToHex(r, g, b) }}
                            role="img" aria-label={`Color preview: ${hex}`} />
                        <div className={styles.outputs}>
                            {this.renderOutputRow('HEX', hex, 'hex')}
                            {this.renderOutputRow('RGB', rgb, 'rgb')}
                            {this.renderOutputRow('HSL', hsl, 'hsl')}
                            {this.renderOutputRow('CMYK', cmyk, 'cmyk')}
                            {this.renderOutputRow('CSS', css, 'css')}
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}
